import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BranchOffice, ENVIRONMENT, LoanOfficer } from '@ld.consumer-portal/models';
import { LoggingService } from '@ld.consumer-portal/utils';
import { firstValueFrom } from 'rxjs';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export type {
  BranchOffice,
  LoanOfficer,
  LoanOfficerResponse,
  LoanOfficerSearchResult,
} from '@ld.consumer-portal/models';

@Injectable({
  providedIn: 'root',
})
export class LoanOfficerService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get mlaApiUrl(): string {
    return (this.env.appSettings.mlaApiUrl || '').replace(/\/+$/, '');
  }

  private readonly defaultFields = [
    'channel',
    'cellPhone',
    'loanOfficerId',
    'nmlsId',
    'city',
    'state',
    'street',
    'zipCode',
    'officePhone',
    'fax',
    'email',
    'preferredName',
    'branchOfficeId',
    'title',
    'headshotUrl',
    'webSiteUrl',
  ];

  async getLoanOfficer(refId: string, fields?: string[]): Promise<LoanOfficer> {
    const fieldsToRequest = fields || this.defaultFields;
    const params = new HttpParams().set('fields', fieldsToRequest.join(','));
    const url = `${this.mlaApiUrl}/api/v1/loan-officer/${refId}`;

    try {
      const response = await firstValueFrom(this.http.get<ApiResponse<LoanOfficer>>(url, { params }));
      return response?.data;
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error fetching loan officer ${refId}`);
      throw new Error(`Failed to fetch loan officer: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getLoanOfficerBasic(refId: string): Promise<LoanOfficer> {
    const basicFields = ['preferredName', 'email', 'cellPhone', 'officePhone'];
    return this.getLoanOfficer(refId, basicFields);
  }

  async getLoanOfficerForDisplay(refId: string): Promise<LoanOfficer> {
    const displayFields = [
      'preferredName', 'email', 'cellPhone', 'officePhone', 'nmlsId',
      'headshotUrl', 'title', 'webSiteUrl', 'street', 'city', 'state', 'zipCode', 'workPhone',
    ];
    return this.getLoanOfficer(refId, displayFields);
  }

  async searchByName(name: string, count?: number, channel?: 'Unknown' | 'Direct' | 'Retail'): Promise<LoanOfficer[]> {
    let params = new HttpParams().set('loanOfficerName', name).set('fields', this.defaultFields.join(','));
    if (count) {
      params = params.set('count', count.toString());
    }
    if (channel) {
      params = params.set('channel', channel);
    }
    const url = `${this.mlaApiUrl}/api/v1/loan-officer`;

    try {
      const response = await firstValueFrom(this.http.get<ApiResponse<LoanOfficer[]>>(url, { params }));
      return response?.data || [];
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error searching loan officers by name "${name}"`);
      throw new Error(`Failed to search loan officers: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async searchByZipCode(zipCode: string): Promise<BranchOffice[]> {
    const params = new HttpParams().set('zipCode', zipCode);
    const url = `${this.mlaApiUrl}/api/v1/branch-office/search`;

    try {
      return await firstValueFrom(this.http.get<BranchOffice[]>(url, { params }));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error searching branches by ZIP "${zipCode}"`);
      throw new Error(`Failed to search branches: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getLoanOfficersByBranch(branchId: string): Promise<LoanOfficer[]> {
    const params = new HttpParams().set('fields', this.defaultFields.join(','));
    const url = `${this.mlaApiUrl}/api/v1/branch-office/${branchId}/loan-officer`;

    try {
      const response = await firstValueFrom(this.http.get<ApiResponse<LoanOfficer[]>>(url, { params }));
      return response?.data || [];
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error fetching loan officers for branch ${branchId}`);
      throw new Error(`Failed to fetch branch loan officers: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getLoanOfficerByName(name: string): Promise<LoanOfficer> {
    const params = new HttpParams().set('fields', 'channel,email,preferredName,loanOfficerId');
    const url = `${this.mlaApiUrl}/api/v1/loan-officer/byname/${encodeURIComponent(name)}`;

    try {
      const response = await firstValueFrom(this.http.get<ApiResponse<LoanOfficer>>(url, { params }));
      return response?.data;
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error fetching loan officer by name "${name}"`);
      throw new Error(`Failed to fetch loan officer: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
}
